import BaseTableController from './base_table_controller';

// dp_id 2300 — data_grid: { rows: [{ id, address, postal_code, city, country, gps_coordinates }] }
export default class extends BaseTableController {
  static values = { countryOptions: Array };

  searchableFields = ['country'];

  connect() {
    const rows = this.initialDataValue?.rows;
    let nextId = 1;
    this.rows =
      Array.isArray(rows) && rows.length > 0
        ? rows.map((r) => {
            const id = r.id != null ? r.id : nextId;
            if (id >= nextId) nextId = id + 1;
            return {
              id,
              address: r.address ?? '',
              postal_code: r.postal_code ?? null,
              city: r.city ?? '',
              country: r.country ?? '',
              gps_coordinates: r.gps_coordinates ?? '',
            };
          })
        : [{ id: 1, address: '', postal_code: null, city: '', country: '', gps_coordinates: '' }];
    this.nextId = Math.max(...this.rows.map((r) => r.id)) + 1;
    this.geocodeTimers = {};
    this.renderRows();
    this.syncHiddenField();
  }

  emptyRow() {
    return {
      id: this.nextId++,
      address: '',
      postal_code: null,
      city: '',
      country: '',
      gps_coordinates: '',
    };
  }

  removeRow(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.rowIndex, 10);
    clearTimeout(this.geocodeTimers[index]);
    this.rows.splice(index, 1);
    if (this.rows.length === 0) this.rows = [this.emptyRow()];
    this.renderRows();
    this.syncHiddenField();
  }

  updateField(event) {
    const el = event.currentTarget;
    const index = parseInt(el.dataset.rowIndex, 10);
    const field = el.dataset.field;
    this.rows[index][field] =
      el.type === 'number' ? (el.value === '' ? null : parseInt(el.value, 10)) : el.value;
    this.syncHiddenField();
    if (['address', 'postal_code', 'country'].includes(field)) this.scheduleGeocode(index);
  }

  scheduleGeocode(index) {
    clearTimeout(this.geocodeTimers[index]);
    this.setGpsCell(index, 'Loading…');
    this.geocodeTimers[index] = setTimeout(() => this.geocodeRow(index), 800);
  }

  async geocodeRow(index) {
    const row = this.rows[index];
    const parts = [row.address, row.postal_code, row.country].filter(Boolean);
    if (parts.length === 0) {
      this.updateGps(index, '');
      return;
    }

    const query = encodeURIComponent(parts.join(', '));
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${query}`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      if (data.length > 0) {
        this.updateGps(
          index,
          `${parseFloat(data[0].lat).toFixed(6)}, ${parseFloat(data[0].lon).toFixed(6)}`
        );
      } else {
        this.updateGps(index, 'Coordinates not found');
      }
    } catch {
      this.updateGps(index, 'Coordinates not found');
    }
  }

  updateGps(index, value) {
    this.rows[index].gps_coordinates = value;
    this.setGpsCell(index, value);
    this.syncHiddenField();
  }

  setGpsCell(index, text) {
    const cell = this.tableBodyTarget.querySelector(`[data-gps-index="${index}"]`);
    if (cell) cell.textContent = text;
  }

  buildRow(row, index) {
    const tr = document.createElement('tr');

    const idTd = document.createElement('td');
    idTd.textContent = row.id;
    idTd.className = 'text-center';
    tr.appendChild(idTd);

    tr.appendChild(this.buildStringCell(row, index, 'address', 'Address'));

    const postalTd = document.createElement('td');
    const postalInput = document.createElement('input');
    postalInput.type = 'number';
    postalInput.min = '0';
    postalInput.step = '1';
    postalInput.placeholder = 'Postal code';
    postalInput.className = 'form-control w-100';
    postalInput.dataset.rowIndex = index;
    postalInput.dataset.field = 'postal_code';
    postalInput.dataset.action = `input->${this.identifier}#updateField`;
    if (row.postal_code !== null && row.postal_code !== undefined)
      postalInput.value = row.postal_code;
    postalTd.appendChild(postalInput);
    tr.appendChild(postalTd);

    tr.appendChild(this.buildStringCell(row, index, 'city', 'City'));
    tr.appendChild(this.buildSelectCell(row, index, 'country', this.countryOptionsValue));

    const gpsTd = document.createElement('td');
    gpsTd.dataset.gpsIndex = index;
    gpsTd.className = 'text-muted small';
    gpsTd.textContent = row.gps_coordinates ?? '';
    tr.appendChild(gpsTd);

    tr.appendChild(this.buildRemoveButton(index));

    return tr;
  }
}
